"use client"

import { Box, Button, Grid, GridItem, Image, Text, useDisclosure, Flex, Avatar, Badge } from "@chakra-ui/react"
import CandImg from "../../../../common/assets/img/userIcon.png"
import colors from "common/theme/foundations/colors"
import CommonCheckTable from "common/components/checktable"
import { useState } from "react"
import { IoIosArrowBack } from "react-icons/io"
import { FiClock, FiBriefcase, FiCalendar, FiUser } from "react-icons/fi"
import { constant } from "common/constant"
import { formatJobTitle } from "services/Crm/utils/calcUtils"
import { useNavigate } from "react-router-dom"
import { isPastDate } from "services/Crm/utils/calcUtils"

export default function InterviewDetailsCard({ cardTitle, showCardDetails = false, showTableCard = false, data, isEmployee = false }) {
  const title = constant.roles
  const [isLoading, setIsLoading] = useState(false)
  const [searchedData, setSearchedData] = useState([])
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [searchboxOutside, setSearchboxOutside] = useState("")
  const [getTagValuesOutSide, setGetTagValuesOutside] = useState([])
  const [displaySearchData, setDisplaySearchData] = useState(false)
  const navigate = useNavigate()

  const [showAllInterviews, setShowAllInterviews] = useState(false)
  const [filteredOpenJobs, setFilteredOpenJobs] = useState([])
  const hasTodaysInterviews = (data?.todaysInterviewList?.length || 0) > 0

  const getTodayDateString = () => {
    const today = new Date()
    return today.toISOString().split("T")[0]
  }

  const formatTime = (timeString) => {
    if (!timeString) return "-"
    try {
      const date = new Date(timeString)
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    } catch (error) {
      console.error("Error formatting time:", error)
      return "-"
    }
  }

  const formatDate = (timeString) => {
    if (!timeString) return "-"
    try {
      return timeString.split("T")[0].split("-").reverse().join("/")
    } catch (error) {
      console.error("Error formatting date:", error)
      return "-"
    }
  }

  const getOpenStatusJobs = (jobs) => {
    if (!jobs || !Array.isArray(jobs)) return []

    // Filter jobs that have 'open', 'in_progress', or 'hold' status
    const activeStatuses = ["open", "in_progress", "hold"]
    return jobs.filter((job) => job.jobStatus && activeStatuses.includes(job.jobStatus.toLowerCase()))
  }

  // Helper function to get displayed items with limit and filter out overdue jobs for Open Jobs
  const getDisplayedItems = (items, showAll, limit = 3) => {
    if (!items || !Array.isArray(items)) return []

    // Filter out overdue jobs for Open Jobs card
    let filteredItems = items
    if (cardTitle === "Open Jobs") {
      filteredItems = items.filter((job) => !isPastDate(job?.targetDate))
    }

    return showAll ? filteredItems : filteredItems.slice(0, limit)
  }

  // PRODUCTION: Client-side sorting as backup (interviews should already be sorted from backend)
  const getSortedInterviews = (interviews) => {
    if (!interviews || !Array.isArray(interviews)) return []

    // Helper function to parse time for sorting
    const parseTimeForSorting = (timeString) => {
      if (!timeString) return 0

      try {
        // If it's already a full datetime, use it directly
        const directDate = new Date(timeString)
        if (!isNaN(directDate.getTime()) && timeString.includes("T")) {
          return directDate.getTime()
        }

        // If it's a time-only string like "04:30 PM", parse it properly
        const timeStr = timeString.toString().trim()

        if (timeStr.includes("AM") || timeStr.includes("PM")) {
          // Format: "04:30 PM" or "4:30 PM"
          const [time, period] = timeStr.split(" ")
          const [hours, minutes] = time.split(":").map(Number)

          let hour24 = hours
          if (period === "PM" && hours !== 12) {
            hour24 = hours + 12
          } else if (period === "AM" && hours === 12) {
            hour24 = 0
          }

          // Create a date object for today with the parsed time
          const today = new Date()
          const parsedTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hour24, minutes, 0)
          return parsedTime.getTime()
        } else {
          // Try to parse as a regular date/time
          const parsedTime = new Date(timeString)
          return isNaN(parsedTime.getTime()) ? 0 : parsedTime.getTime()
        }
      } catch (error) {
        console.error("Error parsing time for sorting:", timeString, error)
        return 0
      }
    }

    // Create a copy and sort by startTime in ascending order (earliest first)
    const sorted = [...interviews].sort((a, b) => {
      // Handle null/undefined startTime
      if (!a.startTime && !b.startTime) return 0
      if (!a.startTime) return 1 // Put interviews without time at the end
      if (!b.startTime) return -1

      try {
        // Use improved time parsing
        const timeA = parseTimeForSorting(a.startTime)
        const timeB = parseTimeForSorting(b.startTime)

        // Primary sort: by start time (ascending - earliest first)
        const timeDiff = timeA - timeB
        if (timeDiff !== 0) return timeDiff

        // Secondary sort: by creation date if times are identical
        const createdA = new Date(a.createdAt || 0).getTime()
        const createdB = new Date(b.createdAt || 0).getTime()
        return createdA - createdB
      } catch (error) {
        console.error("Error sorting interviews:", error)
        return 0
      }
    })

    return sorted
  }

  // Figma Style Columns
  const columns = [
    {
      Header: "Jobs",
      accessor: "title",
      cell: (cell) => (
        <Box>
          <Text me="10px" fontSize="sm" fontWeight="700">
            {formatJobTitle(cell?.value)}
          </Text>
        </Box>
      ),
    },
    {
      Header: "Applied",
      accessor: "totalApplied",
      cell: (cell) => (
        <Badge
          bg={colors.dashboard.badge.appliedBg}
          color={colors.dashboard.badge.appliedText}
          px={3}
          py={1.5}
          borderRadius="6px"
          fontWeight="600"
          fontSize="12px"
          border={`1px solid ${colors.dashboard.badge.appliedBorder}`}
          textTransform="capitalize"
        >
          {cell?.value || 0} Candidates
        </Badge>
      ),
    },
    {
      Header: "Hired",
      accessor: "hiredCount",
      cell: (cell) => (
        <Badge
          bg={colors.dashboard.badge.hiredBg}
          color={colors.dashboard.badge.hiredText}
          px={3}
          py={1.5}
          borderRadius="6px"
          fontWeight="500"
          fontSize="13px"
          border={`1px solid ${colors.dashboard.badge.hiredBorder}`}
          textTransform="capitalize"
        >
          {cell?.value || 0} Candidate
        </Badge>
      ),
    },
    {
      Header: "Rejected",
      accessor: "rejectedCount",
      cell: (cell) => (
        <Badge
          bg={colors.dashboard.badge.rejectedBg}
          color={colors.dashboard.badge.rejectedText}
          px={3}
          py={1.5}
          borderRadius="6px"
          fontWeight="500"
          fontSize="13px"
          border={`1px solid ${colors.dashboard.badge.rejectedBorder}`}
          textTransform="capitalize"
        >
          {cell?.value || 0} Candidates
        </Badge>
      ),
    },
  ]

  return (
    <>
      <Box className="recruit-info-card" p={5}>
        {/* Card Header */}
        <Box className="cardHeader" display="flex" justifyContent="space-between" alignItems="center" >
          <Text className="cardTitle" fontWeight="600" fontSize="lg" color={colors.dashboard.cardHeaderText}>{cardTitle}</Text>

          {cardTitle === "Today's Interviews" && showCardDetails && !isEmployee && hasTodaysInterviews && (
            <Text
              as="button"
              type="button"
              color={colors.dashboard.link}
              fontWeight="500"
              fontSize="14px"
              cursor="pointer"
              bg="transparent"
              border="none"
              p={0}
              m={0}
              _hover={{ textDecoration: "underline", bg: "transparent", color: colors.dashboard.link }}
              _active={{ bg: "transparent" }}
              _focus={{ outline: "none", boxShadow: "none", bg: "transparent" }}
              _focusVisible={{ outline: "none", boxShadow: "none", bg: "transparent" }}
              onClick={() => {
                const params = new URLSearchParams()
                params.set("interviewDate", getTodayDateString())
                navigate(`/recruitment/interviews?${params.toString()}`)
              }}
            >
              See More
            </Text>
          )}

          {cardTitle === "Open Jobs" && data?.openJobs?.filter((job) => !isPastDate(job?.targetDate))?.length > 3 && (
            <Button
              variant="ghost"
              size="sm"
              color={colors.dashboard.link}
              fontWeight="500"
              fontSize="14px"
              _hover={{
                textDecoration: "underline",
              }}
              onClick={() => {
                const params = new URLSearchParams()
                params.set("jobStatus", isEmployee ? "Open" : "Open,In Progress,On Hold")
                navigate(`/recruitment/jobProfile?${params.toString()}`)
              }}
            >
              See More
            </Button>
          )}
          {(cardTitle === "Total Applications" || cardTitle === "Open Job Applications") && data?.jobApplicationStats?.length > 3 && (
            <Button
              variant="ghost"
              size="sm"
              color={colors.dashboard.link}
              fontWeight="500"
              fontSize="14px"
              _hover={{
                textDecoration: "underline",
              }}
              onClick={() => {
                navigate("/recruitment/applications")
              }}
            >
              See More
            </Button>
          )}
        </Box>
        <Box className="cardBody">
          {/* Today's Interviews - Figma Card Style */}
          {showCardDetails && cardTitle === "Today's Interviews" && (
            <Box className="card-details">
              {data && data?.todaysInterviewList?.length ? (
                getSortedInterviews(data.todaysInterviewList).map((item, index) => (
                  <Box
                    key={index}
                    bg={colors.dashboard.cardBg}
                    borderRadius="12px"
                    p={4}
                    mb={3}
                  >
                    <Flex justify="space-between" align="center">
                      <Flex align="center" gap={3}>
                        <Avatar
                          size="md"
                          name={item?.application?.candidateDetails?.name}
                          bg={colors.dashboard.avatar.bg}
                          color={colors.dashboard.avatar.text}
                        />
                        <Box>
                          <Flex align="center" gap={2} mb={1}>
                            <Text fontWeight="600" color={colors.dashboard.cardHeaderText} fontSize="15px">
                              {item?.application?.candidateDetails?.name}
                            </Text>
                            <Badge
                              bg={colors.dashboard.badge.roundBg}
                              color={colors.dashboard.badge.roundText}
                              px={2}
                              py={0.5}
                              borderRadius="4px"
                              fontSize="12px"
                              fontWeight="500"
                            >
                              {item?.round ? (String(item.round).toLowerCase().includes("round") ? item.round : `Round ${item.round}`) : "-"}
                            </Badge>
                          </Flex>
                          <Text color={colors.dashboard.mutedText} fontSize="14px" mb={2}>
                            {formatJobTitle(item?.application?.job?.postingTitle)}
                          </Text>
                          <Flex align="center" gap={4}>
                            <Flex align="center" gap={1}>
                              <FiClock size={14} color={colors.dashboard.iconMuted} />
                              <Text fontSize="13px" color={colors.dashboard.mutedText}>
                                {formatTime(item?.startTime)} - {formatTime(item?.endTime)}
                              </Text>
                            </Flex>
                            <Flex align="center" gap={1}>
                              <FiBriefcase size={14} color={colors.dashboard.iconMuted} />
                              <Text fontSize="13px" color={colors.dashboard.mutedText}>
                                {(item?.application?.candidateDetails?.experienceRange ?? 0) + " Years Experience"}
                              </Text>
                            </Flex>
                          </Flex>
                        </Box>
                      </Flex>
                      <Button
                        bg={colors.dashboard.primaryBtn}
                        color="white"
                        size="sm"
                        borderRadius="6px"
                        fontWeight="500"
                        fontSize="13px"
                        px={4}
                        _hover={{ bg: colors.dashboard.primaryBtnHover }}
                        onClick={() => {
                          sessionStorage.setItem("mode", "overview")
                          navigate(`/recruitment/interview-create/${item._id}`)
                        }}
                      >
                        View Details
                      </Button>
                    </Flex>
                  </Box>
                ))
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                  <Text color={colors.dashboard.mutedText} fontSize="sm">
                    No interviews scheduled for today
                  </Text>
                </Box>
              )}
            </Box>
          )}

          {/* Open Jobs - Figma Card Style */}
          {showCardDetails && cardTitle === "Open Jobs" && (
            <Box className="card-details">
              {data && data?.openJobs?.filter((job) => !isPastDate(job?.targetDate))?.length ? (
                <>
                  {getDisplayedItems(data.openJobs, false, 3).map((item, index) => (
                    <Box
                      key={index}
                      bg={colors.dashboard.cardBg}
                      borderRadius="12px"
                      p={4}
                      mb={3}
                    >
                      <Flex justify="space-between" align="center">
                        <Flex align="center" gap={3}>
                          <Box bg={colors.dashboard.jobIcon.bg} p={3} borderRadius="10px">
                            <FiBriefcase size={20} color={colors.dashboard.jobIcon.icon} />
                          </Box>
                          <Box>
                            <Flex align="center" gap={2} mb={1}>
                              <Text fontWeight="600" color={colors.dashboard.cardHeaderText} fontSize="15px">
                                {formatJobTitle(item?.postingTitle)}
                              </Text>
                              <Badge
                                bg={colors.dashboard.badge.jobStatusBg}
                                color={colors.dashboard.badge.jobStatusText}
                                px={2}
                                py={0.5}
                                borderRadius="4px"
                                fontSize="12px"
                                fontWeight="500"
                              >
                                {isEmployee ? (
                                  ["screening", "submissions", "interview", "offered", "pending"].includes(item?.jobStatus?.toLowerCase())
                                    ? "Open"
                                    : ["hired", "rejected", "archived", "closed", "on-hold"].includes(item?.jobStatus?.toLowerCase())
                                      ? "Closed"
                                      : item?.jobStatus || "screening"
                                ) : (
                                  item?.jobStatus || "screening"
                                )}
                              </Badge>
                            </Flex>
                            <Flex align="center" gap={4}>
                              <Flex align="center" gap={1}>
                                <FiCalendar size={14} color={colors.dashboard.iconMuted} />
                                <Text fontSize="13px" color={colors.dashboard.mutedText}>
                                  Posted: {formatDate(item?.createdAt)}
                                </Text>
                              </Flex>
                              <Flex align="center" gap={1}>
                                <FiUser size={14} color={colors.dashboard.iconMuted} />
                                <Text fontSize="13px" color={colors.dashboard.mutedText}>
                                  By: {item?.createBy?.firstName} {item?.createBy?.lastName || ""}
                                </Text>
                              </Flex>
                            </Flex>
                          </Box>
                        </Flex>
                        <Button
                          bg={colors.dashboard.primaryBtn}
                          color="white"
                          size="sm"
                          borderRadius="6px"
                          fontWeight="500"
                          fontSize="13px"
                          px={4}
                          _hover={{ bg: colors.dashboard.primaryBtnHover }}
                          onClick={() => {
                            sessionStorage.setItem("mode", "overview")
                            navigate(`/recruitment/job-opening-create/${item._id}`)
                          }}
                        >
                          {constant.view}
                        </Button>
                      </Flex>
                    </Box>
                  ))}
                </>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                  <Text color={colors.dashboard.mutedText} fontSize="sm">
                    No open jobs available
                  </Text>
                </Box>
              )}
            </Box>
          )}

          {/* Table Card using CommonCheckTable */}
          {showTableCard && (
            <Box className="table-card">
              <CommonCheckTable
                title={title}
                isLoading={isLoading}
                columnData={columns}
                dataColumn={columns}
                allData={
                  data?.jobApplicationStats?.length > 3
                    ? data.jobApplicationStats.slice(0, 3)
                    : data?.jobApplicationStats || []
                }
                tableData={
                  data?.jobApplicationStats?.length > 3
                    ? data.jobApplicationStats.slice(0, 3)
                    : data?.jobApplicationStats || []
                }
                AdvanceSearch={() => ""}
                tableCustomFields={[]}
                searchedDataOut={searchedData}
                searchDisplay={displaySearchData}
                setSearchDisplay={setDisplaySearchData}
                setSearchedDataOut={setSearchedData}
                searchboxOutside={searchboxOutside}
                setSearchboxOutside={setSearchboxOutside}
                BackButton={
                  <Button className="shape-default" variant="brand" size="sm" leftIcon={<IoIosArrowBack />}>
                    Back
                  </Button>
                }
                deleteMany={true}
                access={true}
                checkBox={false}
                getTagValuesOutSide={getTagValuesOutSide}
                setGetTagValuesOutside={setGetTagValuesOutside}
                ManageGrid={false}
                onOpen={onOpen}
                isHideNavbarAction={false}
                hideSearchBar={true}
              />
            </Box>
          )}
        </Box>
      </Box>
    </>
  )
}
